<div class="materials">

  <!-- ===================================================================== -->
  <!-- ATTACHED ITEMS — search, link filter, and the card grid               -->
  <!-- ===================================================================== -->
  @if (!isPickerVisible) {

    <div class="materials-toolbar">
      <div class="search-box">
        <mat-icon class="search-icon">search</mat-icon>
        <input type="text" class="search-input" placeholder="Search…"
               [(ngModel)]="listSearch" [ngModelOptions]="{ standalone: true }"
               aria-label="Search attached items">
      </div>

      @if (canAddItems) {
        <button type="button" class="add-material-btn" (click)="openPicker()">
          <mat-icon>add</mat-icon>
          <span>Material</span>
        </button>
      }
    </div>

    <!-- EMPTY STATES -->
    @if (items.length === 0) {
      <div class="materials-empty">
        @if (!jobId) {
          <p class="empty-text">Attach this ticket to a job to add materials and services.</p>
        } @else {
          <p class="empty-text">No materials or services added yet.</p>
        }
      </div>
    } @else if (visibleItems.length === 0) {
      <div class="materials-empty">
        <p class="empty-text">No items match your search.</p>
      </div>
    }

    <!-- CARD GRID -->
    @if (visibleItems.length > 0) {
      <div class="item-grid">
        @for (item of visibleItems; track item) {
          <div class="item-card">
            <div class="card-head">
              <mat-icon class="kind-icon">{{ item.kind === 'material' ? 'inventory_2' : 'handyman' }}</mat-icon>

              @if (item.scope_status) {
                <span class="pill pill--approved">
                  <mat-icon>check_circle</mat-icon>{{ item.scope_status }}
                </span>
              }

              @if (!isReadOnly) {
                <button type="button" class="card-remove" matTooltip="Remove item"
                        [attr.aria-label]="'Remove ' + item.item_name"
                        (click)="removeItem(item)">
                  <mat-icon>close</mat-icon>
                </button>
              }
            </div>

            <p class="card-title">{{ item.item_name || '—' }}</p>
            <p class="card-vendor">{{ item.vendor_name || '—' }}</p>

            <div class="card-foot">
              <span class="card-qty">{{ item.quantity ?? '—' }}</span>
              @if (item.price !== null && item.price !== undefined) {
                <span class="card-price">{{ item.price | currency }}</span>
              }
            </div>

            @if (item.is_linked) {
              <span class="scope-chip" [matTooltip]="item.linked_scope_name">{{ item.linked_scope_name }}</span>
            }
          </div>
        }
      </div>
    }
  }

  <!-- ===================================================================== -->
  <!-- PICKER — replaces the list while open                                 -->
  <!-- ===================================================================== -->
  @if (isPickerVisible) {
    <div class="picker">

      <div class="picker-tabs" role="tablist">
        <button type="button" class="picker-tab" role="tab"
                [class.picker-tab--active]="activeTab === 'material'"
                [attr.aria-selected]="activeTab === 'material'"
                (click)="setTab('material')">
          <mat-icon>inventory_2</mat-icon>
          <span>Materials</span>
          <span class="tab-count">{{ pickerCounts.material }}</span>
        </button>

        <button type="button" class="picker-tab" role="tab"
                [class.picker-tab--active]="activeTab === 'service'"
                [attr.aria-selected]="activeTab === 'service'"
                (click)="setTab('service')">
          <mat-icon>handyman</mat-icon>
          <span>Services</span>
          <span class="tab-count">{{ pickerCounts.service }}</span>
        </button>

        @if (canReturnToList) {
          <button type="button" class="picker-close" matTooltip="Back to the item list"
                  aria-label="Close picker and show the added items" (click)="closePicker()">
            <mat-icon>close</mat-icon>
          </button>
        }
      </div>

      <div class="picker-body">
        <!-- Search row. The scope select that used to share this row is
             commented out below, so the search box sits on the left. -->
        <div class="picker-filters">
          <!-- Scope selection hidden for now — restore this block to bring it back.
          <select id="linkScope" class="picker-select" [(ngModel)]="linkedScopeId"
                  [ngModelOptions]="{ standalone: true }"
                  aria-label="Link the added items to a scope (optional)">
            <option [ngValue]="null">No specific scope</option>
            @for (scope of jobScopes; track scope.id) {
              <option [ngValue]="scope.id">{{ scope.scope_name }}</option>
            }
          </select>
          -->

          <div class="search-box picker-search">
            <mat-icon class="search-icon">search</mat-icon>
            <input type="text" class="search-input" placeholder="Search…"
                   [(ngModel)]="pickerSearch" [ngModelOptions]="{ standalone: true }"
                   (keyup.enter)="onPickerSearch()" (change)="onPickerSearch()"
                   aria-label="Search job items">
          </div>
        </div>

        @if (pickerLoading) {
          <div class="picker-skeleton" aria-live="polite" aria-busy="true">
            @for (row of [1, 2, 3, 4]; track row) {
              <div class="skeleton-row">
                <span class="skeleton-box"></span>
                <span class="skeleton-lines">
                  <span class="skeleton-line skeleton-line--title"></span>
                  <span class="skeleton-line skeleton-line--meta"></span>
                </span>
                <span class="skeleton-line skeleton-line--price"></span>
              </div>
            }
          </div>
        } @else if (pickerItems.length === 0) {
          <p class="picker-status">
            No {{ activeTab === 'material' ? 'materials' : 'services' }} on this job.
          </p>
        } @else {
          <div class="picker-list">
            @for (item of pickerItems; track item.id) {
              <div class="picker-row"
                   [class.picker-row--selected]="isSelected(item)"
                   [class.picker-row--added]="isAlreadyAdded(item)"
                   (click)="toggleSelection(item)">
                <mat-checkbox class="bidone-checkbox" [checked]="isSelected(item)"
                              (click)="$event.stopPropagation()"
                              (change)="toggleSelection(item)"
                              [attr.aria-label]="(isAlreadyAdded(item) ? 'Remove ' : 'Select ') + item.item_name">
                </mat-checkbox>

                <div class="picker-row-main">
                  <p class="picker-row-title">
                    {{ item.item_name || '—' }}
                    @if (isAlreadyAdded(item)) {
                      <span class="added-tag">Added</span>
                    }
                  </p>
                  <p class="picker-row-meta">
                    <mat-icon class="meta-icon">store</mat-icon>
                    <span class="meta-vendor">{{ item.vendor_name || '—' }}</span>
                    @if (item.quantity !== null && item.quantity !== undefined) {
                      <span class="meta-dot">·</span>
                      <span class="meta-qty">Qty {{ item.quantity }}</span>
                    }
                  </p>
                </div>

                <div class="picker-row-right">
                  @if (item.price !== null && item.price !== undefined) {
                    <span class="picker-row-price">{{ item.price | currency }}</span>
                  }
                  @if (item.scope_status) {
                    <span class="pill pill--approved">
                      <mat-icon>check_circle</mat-icon>{{ item.scope_status }}
                    </span>
                  }
                </div>
              </div>
            }
          </div>
        }
      </div>

      <div class="picker-foot">
        <span class="selected-count">{{ selectedCount }} selected</span>

        <div class="picker-actions">
          @if (canReturnToList) {
            <button type="button" class="back-btn" (click)="closePicker()">
              <mat-icon>arrow_back</mat-icon>
              <span>Back to list</span>
            </button>
          }

          <button type="button" class="add-selected-btn" [disabled]="!selectedCount"
                  (click)="addSelected()">
            <mat-icon>add</mat-icon>
            <span>Add Selected</span>
          </button>
        </div>
      </div>

    </div>
  }

</div>
